import {
    BlockTypeSelect,
    BoldItalicUnderlineToggles,
    CreateLink,
    diffSourcePlugin,
    DiffSourceToggleWrapper,
    headingsPlugin,
    linkDialogPlugin,
    linkPlugin,
    listsPlugin,
    ListsToggle,
    MDXEditor,
    quotePlugin,
    Separator,
    tablePlugin,
    thematicBreakPlugin,
    toolbarPlugin,
    UndoRedo
} from '@mdxeditor/editor'
import '@mdxeditor/editor/style.css'
import { useCallback, useEffect, useMemo, useState } from "react"
import { v4 } from "uuid"
import api from "../../api"
import { ButtonStatus, CourseType } from "../../common/constants"
import typo from "../../typography.module.css"
import Button from "../Button"
import Select from "../Select"
import styles from "./CommunicationsCard.module.css"
import Uploader from '../Uploader'

const noop = () => {}
const filterSelectOptions = (options, query) => {
    const normalizedQuery = (query || '').toLocaleLowerCase('it').trim()
    if (!normalizedQuery) return options
    return options.filter(option => option.name.toLocaleLowerCase('it').includes(normalizedQuery))
}

const CommunicationsCard = ({onPublish = noop, onDirtyChange = noop}) => {

    const [courses, setCourses] = useState([])
    const [editions, setEditions] = useState([])
    const [modules, setModules] = useState([])
    const [selectedCourse, setSelectedCourse] = useState(null)
    const [selectedEdition, setSelectedEdition] = useState(null)
    const [selectedModule, setSelectedModule] = useState(null)
    const [communication, setCommunication] = useState('')
    const [randomID, setRandomID] = useState(v4())
    const [publishDisabled, setPublishDisabled] = useState(true)
    const [publishState, setPublishState] = useState(null)
    const [medias, setMedias] = useState([])
    const [attachmentsUploading, setAttachmentsUploading] = useState(false)
    const [uploaderReset, setUploaderReset] = useState(0)
    const [loadingCourses, setLoadingCourses] = useState(false)
    const [selectSearch, setSelectSearch] = useState({ course: '', edition: '', module: '' })

    const courseOptions = useMemo(
        () => courses.map(course => ({ id: course.id, name: course.name, value: course.id })),
        [courses]
    )
    const editionOptions = useMemo(
        () => editions.map(edition => ({ id: edition.id, name: edition.name, value: edition.id })),
        [editions]
    )
    const moduleOptions = useMemo(
        () => modules.map(module => ({ id: module.id, name: module.name, value: module.id })),
        [modules]
    )
    const filteredCourseOptions = useMemo(
        () => filterSelectOptions(courseOptions, selectSearch.course),
        [courseOptions, selectSearch.course]
    )
    const filteredEditionOptions = useMemo(
        () => filterSelectOptions(editionOptions, selectSearch.edition),
        [editionOptions, selectSearch.edition]
    )
    const filteredModuleOptions = useMemo(
        () => filterSelectOptions(moduleOptions, selectSearch.module),
        [moduleOptions, selectSearch.module]
    )

    useEffect(() => {
        onDirtyChange(communication.trim() !== '' || medias.length > 0 || attachmentsUploading)
    }, [communication, medias, attachmentsUploading, onDirtyChange])

    const syncMedias = useCallback((uploads = []) => {
        const uniqueMedias = new Map()

        uploads
            .filter(upload => Boolean(upload?.url))
            .forEach(upload => {
                uniqueMedias.set(upload.url, {
                    url: upload.url,
                    name: upload?.file?.name || 'allegato',
                    size: upload?.file?.size || 0,
                })
            })

        setMedias([...uniqueMedias.values()])
        setAttachmentsUploading(uploads.some(upload => upload.status === 'pending' || upload.status === 'in_progress'))
    }, [])

    useEffect(() => {
        const getCourses = async () => {
            setLoadingCourses(true)
            try {
                const c = await api.get(`/admin/courses?complete=true`)
                const availableCourses = c
                    .filter(course => course.type !== CourseType.Webinar && course.archived !== true)
                    .sort((a, b) => a.name.localeCompare(b.name))
                setCourses(availableCourses)
                if(availableCourses.length > 0) {
                    setSelectedCourse(availableCourses[0].id)
                }
            }
            catch (e) {
                console.error(e)
            }
            setLoadingCourses(false)
        }

        getCourses()
    }, [])

    useEffect(() => {
        if (!selectedCourse) {
            setPublishDisabled(true)
            return
        }
        if (courses.find(c => c.id === selectedCourse)?.type === CourseType.Masterclass) {
            setPublishDisabled(selectedEdition === null || selectedModule === null || communication === '')
        }
        if (courses.find(c => c.id === selectedCourse)?.type === CourseType.Professional) {
            setPublishDisabled(selectedEdition === null || communication.trim() === '')
        }
    }, [selectedCourse, selectedEdition, selectedModule, communication, courses])

    const publish = async () => {
        try {
            const uniqueMedias = [...new Map((medias ?? []).map(media => [media.url, media])).values()]
            let data = { message: communication, edition_id: selectedEdition, module_id: selectedModule ?? null, medias: uniqueMedias }
            setPublishState(ButtonStatus.Loading)
            await api.post('/admin/communications', data)
            setCommunication('')
            setMedias([])
            setAttachmentsUploading(false)
            setUploaderReset(current => current + 1)
            setRandomID(v4())
            setPublishState(ButtonStatus.Success)
            onPublish()
        }
        catch (e) {
            console.error(e)
            setPublishState(ButtonStatus.Error)
        }
    }

    useEffect(() => {
        if (publishState === ButtonStatus.Success) {
            setTimeout(() => {
                setPublishState(null)
            }, 2000)
        }
    }, [publishState])

    useEffect(() => {
        if (selectedCourse) {
            setEditions(courses.find(c => c.id === selectedCourse).editions ?? [])
        }
    }, [selectedCourse, courses])

    return (
            <div className={styles.container}>
                {/* <div className={typo.subtitle}>Nuovo Annuncio</div> */}
                {courses &&
                    <>
                        <p className={styles.formHint}>Scegli i destinatari, scrivi il messaggio e aggiungi eventuali allegati.</p>
                        <div className={styles.selectors}>
                            <div className={styles.formField}><span>Corso</span><Select appearance="modern" portal
                            disabled={loadingCourses}
                            options={filteredCourseOptions}
                            selected={courseOptions.find(c => c.id === selectedCourse)}
                            onSearch={(value) => setSelectSearch(previous => ({ ...previous, course: value || '' }))}
                            searchPlaceholder="Cerca corso"
                            placeholder={loadingCourses ? 'Caricando...' : 'Seleziona Corso'} onSelect={(option) => {
                                setSelectedCourse(option.id)
                                if(option.id !== selectedCourse){
                                    setSelectSearch(previous => ({ ...previous, edition: '', module: '' }))
                                    setSelectedEdition(null)
                                    setSelectedModule(null)
                                    setEditions(courses.find(c => c.id === option.id).editions ?? [])
                                    setModules([])
                                }
                            }} /></div>
                            <div className={styles.formField}><span>Edizione</span><Select appearance="modern" portal
                            disabled={loadingCourses}
                            options={filteredEditionOptions}
                            selected={editionOptions.find(e => e.id === selectedEdition)}
                            onSearch={(value) => setSelectSearch(previous => ({ ...previous, edition: value || '' }))}
                            searchPlaceholder="Cerca edizione"
                            onSelect={(option) => {
                                const editionId = option?.value ?? null
                                setSelectedEdition(editionId)
                                if(editionId !== selectedEdition){
                                    setSelectSearch(previous => ({ ...previous, module: '' }))
                                    const selectedEditionData = courses
                                        .find(c => c.id === selectedCourse)
                                        ?.editions?.find(e => e.id === editionId)
                                    setModules(selectedEditionData?.modules ?? [])
                                    setSelectedModule(null)
                                }
                            }} placeholder={loadingCourses ? 'Caricando...' : 'Edizione'} nullable nullLabel="Nessuna edizione" /></div>
                            <div className={styles.formField}><span>Modulo</span><Select appearance="modern" portal
                            disabled={loadingCourses}
                            options={filteredModuleOptions}
                            selected={moduleOptions.find(m => m.id === selectedModule)}
                            onSearch={(value) => setSelectSearch(previous => ({ ...previous, module: value || '' }))}
                            searchPlaceholder="Cerca modulo"
                            onSelect={(option) => {
                                setSelectedModule(option?.value ?? null)
                            }} placeholder={loadingCourses ? 'Caricando...' : 'Modulo'} nullable nullLabel="Nessun modulo" /></div>
                        </div>
                        <MDXEditor
                            key={randomID}
                            className={styles.editor}
                            contentEditableClassName={styles.editorContent}
                            disabled={publishState === ButtonStatus.Loading}
                            markdown={communication}
                            onChange={(value) => {
                                setCommunication(value)
                            }}
                            placeholder={"Scrivi qui..."}
                            plugins={[
                                headingsPlugin(),
                                linkPlugin(),
                                listsPlugin(),
                                quotePlugin(),
                                thematicBreakPlugin(),
                                linkDialogPlugin(),
                                diffSourcePlugin(),
                                tablePlugin(),
                                toolbarPlugin({
                                    toolbarContents: () => (
                                        <>
                                            {' '}
                                            <UndoRedo />
                                            <Separator />
                                            <BoldItalicUnderlineToggles />
                                            <Separator />
                                            <ListsToggle />
                                            <CreateLink />
                                            <Separator />
                                            <BlockTypeSelect />
                                            <DiffSourceToggleWrapper options={["rich-text", "source"]} />
                                        </>
                                    )
                                })
                            ]}
                        />
                        <div className={styles.attachments}>
                            <div className={`${typo.headline} ${styles.attachmentsHeader}`}>Allegati</div>
                            <Uploader
                                multiple
                                reset={uploaderReset}
                                onStart={() => setAttachmentsUploading(true)}
                                onProgress={(_, uploads) => syncMedias(uploads)}
                                onComplete={syncMedias}
                                onDelete={syncMedias}
                            />
                        </div>
                        <div className={styles.actions}>
                            <Button
                                style={{ padding: '.7rem 1rem', fontSize: '.75rem', borderRadius: 6 }}
                                disabled={publishDisabled || attachmentsUploading || publishState === ButtonStatus.Loading}
                                status={publishState}
                                onClick={publish}>
                                Invia annuncio
                            </Button>
                        </div>
                    </>
                }
            </div>
    )

}

export default CommunicationsCard
